import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import Container from '@/components/base/container'
import CtaButton from '@/components/base/cta-button'
import CustomSelect from '@/components/base/custom-select'
import Grid from '@/components/base/grid'
import Section from '@/components/base/section'
import Spinner from '@/components/base/spinner'
import Typography from '@/components/base/typography'
import Spacer from '@/components/sections/spacer'
import { useStore } from '@/contexts/store'
import { API } from '@/constants'
import { axiosServer } from '@/base/axios'

import { cn } from '@/lib/utils'
import { WPPageData } from '@/types'

type AreaGeograficaTerm = {
  term_id: number
  name: string
  slug: string
  term_group: number
  term_taxonomy_id: number
  taxonomy: string
  description: string
  parent: number
  count: number
  filter: string
  term_order: string
}

type Agency = {
  id: number
  title: { rendered: string }
  acf: {
    address: {
      complete_address: string
    }
    email: {
      to: string
    }
    phone: {
      phone_1: string
    }
    referents: {
      referent_1: {
        email: string
        name: string
        phone: string
      }
      referent_2: {
        email: string
        name: string
        phone: string
      }
      referent_3: {
        email: string
        name: string
        phone: string
      }
    }
  }
  taxonomies: {
    agency_type: {
      count: number
      description: string
      name: string
      filter: string
      parent: 0
      slug: string
      term_group: number
      term_id: number
      term_taxonomy_id: number
      taxonomy: string
    }[]
  }
}

export type AgenciesProps = Partial<{
  pageProps: WPPageData & {
    area_geografica?: AreaGeograficaTerm[]
  }
}>

const Agencies = ({ pageProps, ...props }: AgenciesProps) => {
  const { strings } = useStore()

  const allTerms = useMemo(
    () => (pageProps?.area_geografica ?? []).filter((t) => t.count > 0),
    [pageProps?.area_geografica]
  )

  // Valori selezionati
  const [selectedLevel1, setSelectedLevel1] = useState<string>('')
  const [selectedLevel2, setSelectedLevel2] = useState<string>('')
  const [selectedLevel3, setSelectedLevel3] = useState<string>('')

  // Risultati
  const [agencies, setAgencies] = useState<Agency[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const currentTermRef = useRef<number | null>(null)

  // Reset keys per forzare il reset delle select
  const [resetKey2, setResetKey2] = useState(0)
  const [resetKey3, setResetKey3] = useState(0)

  // Termini derivati staticamente da pageProps
  const level1Terms = useMemo(
    () => allTerms.filter((t) => t.parent === 0),
    [allTerms]
  )

  const level2Terms = useMemo(
    () =>
      selectedLevel1
        ? allTerms.filter((t) => t.parent === Number(selectedLevel1))
        : [],
    [allTerms, selectedLevel1]
  )

  const level3Terms = useMemo(
    () =>
      selectedLevel2
        ? allTerms.filter((t) => t.parent === Number(selectedLevel2))
        : [],
    [allTerms, selectedLevel2]
  )

  const PER_PAGE = 12

  // Fetch agenzie per un termine specifico (o tutte)
  const fetchAgencies = useCallback(async (termId?: number, page = 1) => {
    if (page === 1) {
      setLoading(true)
    } else {
      setLoadingMore(true)
    }
    currentTermRef.current = termId ?? null
    try {
      const params: Record<string, string | number> = {
        per_page: PER_PAGE,
        page
      }
      if (termId) {
        params['area-geografica'] = termId
      }
      const response = await axiosServer.get<Agency[]>(`${API.BASE}/agency`, {
        params
      })
      const total = Number(response.headers?.['x-wp-totalpages']) || 1
      setTotalPages(total)
      setCurrentPage(page)
      if (page === 1) {
        setAgencies(response.data)
      } else {
        setAgencies((prev) => [...prev, ...response.data])
      }
    } catch {
      if (page === 1) setAgencies([])
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [])

  // Load more
  const handleLoadMore = useCallback(() => {
    if (loadingMore || currentPage >= totalPages) return
    fetchAgencies(currentTermRef.current ?? undefined, currentPage + 1)
  }, [loadingMore, currentPage, totalPages, fetchAgencies])

  // Fetch iniziale al mount
  useEffect(() => {
    fetchAgencies()
  }, [fetchAgencies])

  // Handler selezione livello 1
  const handleLevel1Change = useCallback(
    (value: string) => {
      setSelectedLevel1(value)
      setSelectedLevel2('')
      setSelectedLevel3('')
      setResetKey2((k) => k + 1)
      setResetKey3((k) => k + 1)

      fetchAgencies(Number(value), 1)
    },
    [fetchAgencies]
  )

  // Handler selezione livello 2
  const handleLevel2Change = useCallback(
    (value: string) => {
      setSelectedLevel2(value)
      setSelectedLevel3('')
      setResetKey3((k) => k + 1)

      fetchAgencies(Number(value), 1)
    },
    [fetchAgencies]
  )

  // Handler selezione livello 3
  const handleLevel3Change = useCallback(
    (value: string) => {
      setSelectedLevel3(value)
      fetchAgencies(Number(value), 1)
    },
    [fetchAgencies]
  )

  // Reset handlers
  const handleLevel1Reset = useCallback(() => {
    setSelectedLevel1('')
    setSelectedLevel2('')
    setSelectedLevel3('')
    setResetKey2((k) => k + 1)
    setResetKey3((k) => k + 1)

    fetchAgencies(undefined, 1)
  }, [fetchAgencies])

  const handleLevel2Reset = useCallback(() => {
    setSelectedLevel2('')
    setSelectedLevel3('')
    setResetKey3((k) => k + 1)

    if (selectedLevel1) {
      fetchAgencies(Number(selectedLevel1), 1)
    }
  }, [selectedLevel1, fetchAgencies])

  const handleLevel3Reset = useCallback(() => {
    setSelectedLevel3('')

    if (selectedLevel2) {
      fetchAgencies(Number(selectedLevel2), 1)
    }
  }, [selectedLevel2, fetchAgencies])

  // Opzioni per le select
  const level1Options = level1Terms
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((t) => ({
      value: String(t.term_id),
      text: t.name
    }))
  const level2Options = level2Terms
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((t) => ({
      value: String(t.term_id),
      text: t.name
    }))
  const level3Options = level3Terms
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((t) => ({
      value: String(t.term_id),
      text: t.name
    }))

  return (
    <Section
      className={cn(['min-h-[calc(var(--vh)*100-var(--header-height))]'])}
      {...props}
      data-agencies
    >
      <Spacer variant="xxl" />
      <Spacer variant="xxl" />

      {/* Filtri */}
      <Container>
        <Grid className="gap-y-spacing-md">
          <div className="col-span-2 md:col-span-6 lg:col-span-4">
            <Typography
              variant="h2"
              component="h1"
              className="flex text-text-heading-light dark:text-text-heading-dark"
            >
              {pageProps?.title.rendered}
            </Typography>
          </div>

          {/* Select Livello 1 */}
          <div
            className={cn([
              'col-span-2 md:col-span-6 lg:col-span-8',
              'grid grid-cols-1 gap-spacing-3xs md:grid-cols-3 md:gap-0'
            ])}
          >
            <CustomSelect
              variant="outlined"
              options={level1Options}
              placeholder={strings?.labels?.agencies_select_area}
              value={selectedLevel1 || undefined}
              onChange={handleLevel1Change}
              onReset={handleLevel1Reset}
            />

            {/* Select Livello 2 */}
            <CustomSelect
              variant="outlined"
              resetKey={resetKey2}
              options={level2Options}
              placeholder={strings?.labels?.agencies_select_region}
              value={selectedLevel2 || undefined}
              onChange={handleLevel2Change}
              onReset={handleLevel2Reset}
              disabled={level2Terms.length === 0 || loading}
            />

            {/* Select Livello 3 */}
            <CustomSelect
              variant="outlined"
              resetKey={resetKey3}
              options={level3Options}
              placeholder={strings?.labels?.agencies_select_city}
              value={selectedLevel3 || undefined}
              onChange={handleLevel3Change}
              onReset={handleLevel3Reset}
              disabled={level3Terms.length === 0 || loading}
            />
          </div>
        </Grid>
      </Container>

      <Spacer variant="lg" />

      {/* Risultati */}
      <Container>
        {loading ? (
          <div className="flex items-center justify-center py-spacing-xl">
            <Spinner variant="medium" />
          </div>
        ) : agencies.length > 0 ? (
          <>
            <Typography
              variant="small"
              className="text-text-tertiary-light dark:text-text-tertiary-dark mb-spacing-md"
            >
              {agencies.length}{' '}
              {agencies.length === 1
                ? strings?.labels?.agencies_result_count || 'risultato'
                : strings?.labels?.agencies_results_count || 'risultati'}
            </Typography>

            <Grid className="gap-y-spacing-md">
              {agencies.map((agency) => (
                <div
                  key={agency.id}
                  className={cn(
                    'col-span-2 md:col-span-3 lg:col-span-4',
                    'rounded-radius-minimal border border-[rgba(255,255,255,0.2)] p-spacing-sm',
                    'transition-shadow duration-300 hover:bg-border-primary-light hover:shadow-lg'
                  )}
                >
                  {agency?.taxonomies?.agency_type?.length > 0 ? (
                    <>
                      <div className="inline-flex h-[3.2rem] items-center border border-[rgba(255,255,255,0.2)] px-spacing-2xs">
                        <Typography
                          variant="regular"
                          className="inline-flex text-text-primary-dark"
                        >
                          {agency.taxonomies.agency_type
                            .map((t) => t.name)
                            .join(', ')}
                        </Typography>
                      </div>

                      <Spacer variant="lg" />
                    </>
                  ) : null}

                  <div className="flex flex-col gap-spacing-sm">
                    <Typography
                      variant="h5"
                      component="h3"
                      className="text-text-heading-dark"
                    >
                      {agency.title.rendered}
                    </Typography>

                    {agency.acf?.address?.complete_address && (
                      <Typography
                        variant="large"
                        className="text-text-heading-dark"
                      >
                        {agency.acf.address.complete_address}
                      </Typography>
                    )}

                    {agency.acf?.email?.to && (
                      <Typography
                        variant="large"
                        className="text-text-heading-dark"
                      >
                        E-mail:{' '}
                        <a href={`mailto:${agency.acf.email.to}`}>
                          {agency.acf.email.to}
                        </a>
                      </Typography>
                    )}

                    {agency.acf?.phone?.phone_1 && (
                      <Typography
                        variant="large"
                        className="text-text-heading-dark"
                      >
                        Tel:{' '}
                        <a href={`tel:${agency.acf.phone.phone_1}`}>
                          {agency.acf.phone.phone_1}
                        </a>
                      </Typography>
                    )}
                  </div>

                  <Spacer variant="lg" />

                  <div className="flex flex-col gap-y-spacing-md">
                    {agency.acf?.referents?.referent_1?.name ? (
                      <div className="flex flex-col items-end gap-y-spacing-xs">
                        <Typography
                          variant="regular"
                          className="text-right text-text-heading-dark"
                        >
                          {agency.acf.referents.referent_1.name}
                        </Typography>

                        <Typography
                          variant="regular"
                          className="text-right text-text-primary-invert-light"
                        >
                          {agency?.acf?.referents?.referent_1?.phone ? (
                            <>
                              Tel:{' '}
                              <a
                                href={`tel:${agency.acf.referents.referent_1.phone}`}
                              >
                                {agency.acf.referents.referent_1.phone}
                              </a>
                              <br />
                            </>
                          ) : null}
                          {agency?.acf?.referents?.referent_1?.email ? (
                            <>
                              E-mail:{' '}
                              <a
                                href={`mailto:${agency.acf.referents.referent_1.email}`}
                              >
                                {agency.acf.referents.referent_1.email}
                              </a>
                            </>
                          ) : null}
                        </Typography>
                      </div>
                    ) : null}

                    {agency.acf?.referents?.referent_2?.name ? (
                      <div className="flex flex-col items-end gap-y-spacing-xs">
                        <Typography
                          variant="regular"
                          className="text-right text-text-heading-dark"
                        >
                          {agency.acf.referents.referent_2.name}
                        </Typography>

                        <Typography
                          variant="regular"
                          className="text-right text-text-primary-invert-light"
                        >
                          {agency?.acf?.referents?.referent_2?.phone ? (
                            <>
                              Tel:{' '}
                              <a
                                href={`tel:${agency.acf.referents.referent_2.phone}`}
                              >
                                {agency.acf.referents.referent_2.phone}
                              </a>
                              <br />
                            </>
                          ) : null}
                          {agency?.acf?.referents?.referent_2?.email ? (
                            <>
                              E-mail:{' '}
                              <a
                                href={`mailto:${agency.acf.referents.referent_2.email}`}
                              >
                                {agency.acf.referents.referent_2.email}
                              </a>
                            </>
                          ) : null}
                        </Typography>
                      </div>
                    ) : null}

                    {agency.acf?.referents?.referent_3?.name ? (
                      <div className="flex flex-col items-end gap-y-spacing-xs">
                        <Typography
                          variant="regular"
                          className="text-right text-text-heading-dark"
                        >
                          {agency.acf.referents.referent_3.name}
                        </Typography>

                        <Typography
                          variant="regular"
                          className="text-right text-text-primary-invert-light"
                        >
                          {agency?.acf?.referents?.referent_3?.phone ? (
                            <>
                              Tel:{' '}
                              <a
                                href={`tel:${agency.acf.referents.referent_3.phone}`}
                              >
                                {agency.acf.referents.referent_3.phone}
                              </a>
                              <br />
                            </>
                          ) : null}
                          {agency?.acf?.referents?.referent_3?.email ? (
                            <>
                              E-mail:{' '}
                              <a
                                href={`mailto:${agency.acf.referents.referent_3.email}`}
                              >
                                {agency.acf.referents.referent_3.email}
                              </a>
                            </>
                          ) : null}
                        </Typography>
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </Grid>

            {currentPage < totalPages && (
              <>
                <Spacer variant="lg" />
                <div className="flex justify-center">
                  <CtaButton
                    variant="primary"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? (
                      <Spinner variant="small" />
                    ) : (
                      strings?.labels?.load_more
                    )}
                  </CtaButton>
                </div>
              </>
            )}
          </>
        ) : !loading && agencies.length === 0 ? (
          <div className="flex items-center justify-center py-spacing-xl">
            <Typography
              variant="regular"
              className="text-text-tertiary-light dark:text-text-tertiary-dark"
            >
              {strings?.labels?.agencies_no_results}
            </Typography>
          </div>
        ) : null}
      </Container>

      <Spacer variant="xl" />
    </Section>
  )
}

export default Agencies
